import type { Root } from "../../domain/app-types.ts";
import type { Category, Todo } from "../../domain/entities.ts";
import type { RoutedActionHandler } from "../../app/actions.ts";
import { escapeHtml } from "../../ui/text.ts";

export interface TaskLocation {
  taskId: string;
  root: { name: string; canOpen: boolean };
  collection: { name: string; canOpen: boolean };
}

export function taskLocation(todo: Todo, roots: Root[], categories: Category[], canOpenSpace: (id: string) => boolean): TaskLocation {
  const root = roots.find(item => item.id === todo.rootId);
  const collection = categories.find(item => item.id === todo.categoryId && item.rootId === todo.rootId && !item.deleted);
  return {
    taskId: todo.id,
    root: { name: root?.name || "Shared task", canOpen: !!root && !root.archived_at && canOpenSpace(root.id) },
    collection: { name: collection ? `${collection.name}${collection.archivedAt ? " (archived)" : ""}` : todo.categoryId ? "Unavailable collection" : "No collection", canOpen: !!collection && !collection.archivedAt && !root?.archived_at },
  };
}

export function taskLocationView(location: TaskLocation): string {
  const part = (kind: "space" | "collection", item: TaskLocation["root"]) => item.canOpen
    ? `<button type="button" data-action="open-task-location" data-id="${escapeHtml(location.taskId)}" data-destination="${kind}" aria-label="Open ${kind} ${escapeHtml(item.name)}">${escapeHtml(item.name)}</button>`
    : `<span>${escapeHtml(item.name)}</span>`;
  return `<nav class="task-location" aria-label="Task location">${part("space", location.root)}<span class="task-location__separator" aria-hidden="true">›</span>${part("collection", location.collection)}</nav>`;
}

export interface TaskLocationCapabilities {
  locate(id: string): Promise<{ task: Todo; location: TaskLocation } | undefined>;
  navigate(rootId: string, collectionId: string | null): Promise<void>;
  unavailable(): void;
}

export function createTaskLocationActionHandler(capabilities: TaskLocationCapabilities): RoutedActionHandler {
  return async (name, target) => {
    if (name !== "open-task-location") return false;
    try {
      const current = await capabilities.locate(target.dataset.id || "");
      const kind = target.dataset.destination;
      if (!current || kind !== "space" && kind !== "collection" || !(kind === "space" ? current.location.root.canOpen : current.location.collection.canOpen)) {
        capabilities.unavailable(); return true;
      }
      await capabilities.navigate(current.task.rootId, kind === "collection" ? current.task.categoryId : null);
    } catch { capabilities.unavailable(); }
    return true;
  };
}
